import { TaskHistoryMappingFields } from "../../task-management/update-task/models/update-task-model.model";
import { TaskResponse } from "../../task-management/models/task-response";
import { UserAccount } from "../../../models/user-account";

export class Task {}

export const UpdateTaskDialogConstants = {
    minWidth: "85vw",
    maxWidth: "85vw",
    height: "",
    panelClass: "update-task-dialog-container",
};

export class TimeTrackingRecord {
    entityId: number;
    entityTypeId: number;
    firstName: string;
    isBilled: boolean;
    middleName: string | null;
    lastName: string;
    timeTrackingId: number;
    recordedEffortMins: number;
    recordedEarnedTime: number;
    recordedEffortDate: string;
    lastUpdatedDateTime: string;
    isEditable: boolean;
    entityNumber?: string;

    constructor(data: Partial<TimeTrackingRecord>) {
        Object.assign(this, data);
    }

    // Example getter method
    get fullName(): string {
        return `${this.firstName} ${this.middleName ? this.middleName + " " : ""}${this.lastName}`;
    }
}

export class TimeTrackingResponse {
    taskId: number;
    taskNumber: string;
    taskTypeId: number;
    teamId: number;
    teamCode: string;
    recordedEffortsByDateTimeList: TimeTrackingRecordDateWiseKeyValue;
}

export class TimeTrackingRecordDateWiseKeyValue {
    [date: string]: TimeTrackingRecord[];
}

export class NewTimeRecordDataResponse {
    effortsGroupedByDate: NewTimeTrackingRecordDateWise[];
    totalEffort: number;
    totalEarned: number;
}

export class NewTimeTrackingRecordDateWise {
    date: string;
    totalEffort: number;
    totalEarned: number;
    tasksEfforts: ExtendedTimeTrackingRecord[];
    meetingsEfforts: ExtendedMeetingTimeTrackingRecord[];
}

export class MeetingTimeTrackingRecord {
    taskNumber: number | string;
    meetingNumber: number | string;
    totalMeetingsEffort: number;
    efforts: TimeTrackingRecord[];
}

export class ExtendedMeetingTimeTrackingRecord extends MeetingTimeTrackingRecord {
    taskType: number;

    constructor(data: Partial<MeetingTimeTrackingRecord>, taskType: number) {
        super();
        Object.assign(this, data);
        this.taskType = taskType;
    }
}

export class ExtendedTimeTrackingRecord extends TimeTrackingRecord {
    taskNumber: string;
    taskType: number;

    constructor(data: Partial<TimeTrackingRecord>, taskNumber: string, taskType: number) {
        super(data);
        this.taskNumber = taskNumber;
        this.taskType = taskType;
    }
}

export interface Severity {
    severityId: number;
    severityDescription: string;
    severityDisplayName: string;
}

export interface Resolution {
    resolutionId: number;
    resolutionDescription: string;
    resolutionDisplayName: string;
}

export interface BugEnvironment {
    environmentId: number;
    environmentDescription: string;
    environmentDisplayName: string;
    isActive: boolean;
}

export class GetAllEnvironmentSeverityResolution {
    environment: BugEnvironment[] = [];
    severity: Severity[] = [];
    resolution: Resolution[] = [];
}

export enum PlaceOfIdentification {
    INTERNAL = "Internal",
    EXTERNAL = "External",
    UNKNOWN = "Unknown",
}

export type PlaceOfIdentificationType = {
    id: string;
    displayName: string;
};

export function getPlaceOfIdentificationOptions(): Array<PlaceOfIdentificationType> {
    return Object.entries(PlaceOfIdentification).map(([key, value]) => ({
        id: key,
        displayName: value,
    }));
}

export interface SearchTaskResponse {
    taskNumber: string | number;
    taskTitle: string;
}

export type BugFieldsOutPutEventData = {
    selectedTeam: number | undefined | null;
    selectedOrgId: number | undefined | null;
    selectedEnvironmentId: number | undefined | null;
    selectedSeverityId: number | undefined | null;
    selectedResolutionId: number | undefined | null;
    selectedPlaceOfIdentificationId: string | undefined | null;
    selectedAssociatedTask: SearchTaskResponse[] | undefined | null;
    isCustomerImpacted: boolean | undefined | null;
    isBugFieldsFormValid: boolean;
    stepsTakenToCompleteText: string | undefined | null;
    rcaId: number | null;
    isRcaDone: boolean | null;
    rcaReason: string | null;
    rcaIntroducedBy: number[] | null;
    fkAccountIdBugReportedBy: UserAccount | null;
    removedExistingAssociatedTasks: LinkedTaskListForUpdateTask[];
};

export class LinkedTaskListForUpdateTask {
    taskNumber: string;
    taskId: number;
    taskTitle: string;
    taskDesc: string;
    taskExpStartDate: string | null;
    taskExpEndDate: string | null;
    taskEstimate: number | null;
    accountIdAssigned: number | null;
    taskProgressSystem: string | null;
    systemDerivedEndTs: string | null;
    userPerceivedPercentageTaskCompleted: number | null;
    recordedEffort: number | null;
    workflowTaskStatus: string | null;
}

export class OpenTaskRequestModel {
    taskNumber: string | number | null;
    taskIdentifier: number | null;
    teamId: number | null;
    orgId: number | null;
}

export class OpenWorkItemDialogData {
    openTaskRequestModel: OpenTaskRequestModel;
    aiResponse: any;
    scrollTo: number | null;
    isDuplicateTask: boolean | null;
    duplicateTaskResponse: any;
    navigateToPerformanceFeedback: boolean | null; // to navigate to performance feedback tab
}

export const SCROLL_TO = {
    BUG_FIELDS_SECTION: 'bugFieldsSection',
    NOTES: 'notesSection'
};

export class WorkItemHistoryDialogData {
    task: TaskResponse | null | undefined;
    preSelectedTaskHistoryMappingFields: TaskHistoryMappingFields[] | null = null;
    openTaskRequestModel: OpenTaskRequestModel | null | undefined;
}

export enum DeletionReason {
    DUPLICATE = 1,
    CHANGE_IN_REQUIREMENT = 2,
    NO_LONGER_REQUIRED = 3,
    OTHERS = 4,
}

export const DeletionReasonLabels: Record<DeletionReason, string> = {
    [DeletionReason.DUPLICATE]: "Duplicate work item",
    [DeletionReason.CHANGE_IN_REQUIREMENT]: "Change in requirement",
    [DeletionReason.NO_LONGER_REQUIRED]: "No longer required",
    [DeletionReason.OTHERS]: "Others",
};

export interface WorkItemDeleteReasonsRequest {
    deleteReasonId?: number | null; // optional if task in backlog
    duplicateWorkItemNumber?: string | null; // required if reason is DUPLICATE
    deleteReason?: string | null; // required if reason is OTHERS
    removeFromSprint? : boolean | null; // optional if task in sprint
}

export enum RCARequired {
    REQUIRED = 1,
    NOT_REQUIRED = 2,
    MAY_BE_REQUIRED = 3,
}

export const rcaRequiredOptions: Record<RCARequired, string> = {
    [RCARequired.REQUIRED]: "Required",
    [RCARequired.NOT_REQUIRED]: "Not Required",
    [RCARequired.MAY_BE_REQUIRED]: "May Be Required",
};

export interface ReferenceMeetingDialogData {
    taskId: number;
    meetingId: number;
    message: string;
    allowedActions: string[];
    notificationSentTime?: string | null;
}

export enum ReferenceMeetingAction {
    SEND_NOTIFICATION = 1,
    ALL_ATTENDED_PROVIDED_INPUT = 2
}

export const ReferenceMeetingActionLabels = {
    SEND_NOTIFICATION: 'Send Notification(s)',
    ALL_ATTENDED_PROVIDED_INPUT: 'All who attended have provided inputs',
    CLOSE: 'Close'
}
